import React, { useEffect, useState } from 'react';
import { Chip, DialogTitle, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import BuildIcon from '@mui/icons-material/Build';
import {
  fetchImmobilisations,
  setSelectedImmobilisation,
  clearImmobilisationData,
  clearMessages,
  deleteImmobilisation,
} from '../reduxStore/ImmobilisationSlice';
import { AppDispatch, RootState } from '../reduxStore/Store';
import { Immobilisation } from '../models/models';
import { TableHeader, TableAction } from '../constants/interfaces';
import { ButtonType } from '../constants/enums';
import MyTable from '../components/MyTable';
import { useDialog } from '../components/DialogComponent';
import ImmobilisationForm from '../components/Forms/ImmobilisationForm';
import { getLabels } from '../constants/fieldLabels';
import AppPage from '../components/layout/AppPage';
import AppSurface from '../components/layout/AppSurface';
import AppPageHeader from '../components/layout/AppPageHeader';

const formatNumber = (v: number | string | null | undefined): string => {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatDate = (d: string | null | undefined): string => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('fr-FR');
};

const GestionImmobilisations: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    immobilisationData,
    total,
    loading,
    error,
    successMessage,
    selectedImmobilisation,
  } = useSelector((state: RootState) => state.ImmobilisationSlice);

  const [fetchParams, setFetchParams] = useState({ limit: 10, offset: 0 });

  const [FormDialog, openDialog, closeDialog] = useDialog({
    dialogProps: { maxWidth: 'sm', fullWidth: true },
  });

  useEffect(() => {
    dispatch(fetchImmobilisations(fetchParams));
    return () => {
      dispatch(setSelectedImmobilisation(null));
      dispatch(clearImmobilisationData());
      dispatch(clearMessages());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, fetchParams, successMessage]);

  const handleAdd = () => {
    dispatch(setSelectedImmobilisation(null));
    openDialog();
  };

  const handleClose = () => {
    closeDialog();
    dispatch(setSelectedImmobilisation(null));
  };

  const handlePageChange = (newPage: number, newRowsPerPage: number) => {
    setFetchParams({ limit: newRowsPerPage, offset: newPage * newRowsPerPage });
  };

  const headers: TableHeader<Immobilisation>[] = [
    { key: 'libelle', label: 'Libellé' },
    {
      key: 'valeur_acquisition',
      label: "Valeur d'acquisition",
      align: 'right',
      render: r => formatNumber(r.valeur_acquisition),
    },
    {
      key: 'date_acquisition',
      label: 'Acquise le',
      render: r => formatDate(r.date_acquisition),
    },
    {
      key: 'vnc',
      label: 'VNC',
      align: 'right',
      render: r => formatNumber(r.vnc),
    },
    {
      key: 'date_cession',
      label: 'Statut',
      render: r =>
        r.date_cession ? (
          <Chip label={`Cédé le ${formatDate(r.date_cession)}`} size="small" color="warning" />
        ) : (
          <Chip label="Détenu" size="small" color="success" />
        ),
    },
  ];

  const tableActions: TableAction<Immobilisation>[] = [
    {
      label: 'Modifier',
      type: ButtonType.EDIT,
      callback: row => {
        dispatch(setSelectedImmobilisation(row));
        openDialog();
      },
    },
    {
      label: 'Supprimer',
      type: ButtonType.DELETE,
      callback: row => {
        if (row.id && window.confirm(`Supprimer l'immobilisation « ${row.libelle} » ?`)) {
          dispatch(deleteImmobilisation(row.id));
        }
      },
      // Verrou : pas de delete depuis ici si elle provient d'une facture
      // (la suppression doit passer par la ligne de facture).
      disabled: row => !!row.ligne_document_id,
    },
  ];

  return (
    <AppPage maxWidth={false}>
      <AppPageHeader
        title="Immobilisations"
        subtitle={`${total} ${total > 1 ? 'biens' : 'bien'}`}
        icon={<BuildIcon />}
        addLabel="Ajouter une immobilisation"
        onAdd={handleAdd}
      />

      {loading && <Typography color="#667085" mb={2}>Chargement...</Typography>}
      {error && <Typography color="#b42318" mb={2}>Erreur : {error}</Typography>}

      <AppSurface>
        <MyTable
          headers={headers}
          data={immobilisationData}
          actions={tableActions}
          totalRows={total}
          onPageChange={handlePageChange}
          showDetails
          fieldLabels={getLabels('Immobilisation')}
        />
      </AppSurface>

      <FormDialog>
        <DialogTitle>
          <Typography variant="h6">
            {selectedImmobilisation ? 'Modifier une immobilisation' : 'Nouvelle immobilisation'}
          </Typography>
        </DialogTitle>
        <ImmobilisationForm
          selectedImmobilisation={selectedImmobilisation}
          onClose={handleClose}
        />
      </FormDialog>
    </AppPage>
  );
};

export default GestionImmobilisations;
